import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR, API_BASE, downloadVoucher, printTicket } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Calendar, Users, Download, X, ArrowRight, Ticket, MapPin, MessageSquare, Printer } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-green-100 text-green-800 border-green-200",
  voucher_issued: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  refunded: "bg-gray-100 text-gray-800 border-gray-200",
};

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/bookings/me").then((r) => setBookings(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const cancel = async (b) => {
    if (!window.confirm(`Cancel booking ${b.reference_no}?`)) return;
    try {
      await api.post(`/bookings/${b.id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (e) {
      toast.error("Cancel failed");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="my-bookings-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl">My Bookings</h1>
          <p className="text-muted-foreground text-sm mt-1">View and manage all your trips</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : bookings.length === 0 ? (
        <div className="bg-white border border-border rounded-2xl p-12 text-center" data-testid="no-bookings">
          <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-display text-xl mb-1">No bookings yet</h3>
          <p className="text-sm text-muted-foreground mb-5">Start exploring and book your first experience.</p>
          <Link to="/"><Button className="bg-saffron hover:bg-saffron-dark text-white">Browse Tours</Button></Link>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => (
            <div key={b.id} className="bg-white border border-border rounded-2xl p-5 sm:p-6" data-testid={`booking-${b.reference_no}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Badge variant="outline" className="capitalize border-teal text-teal">{b.product_category}</Badge>
                    <Badge className={`${STATUS_COLORS[b.status]} border capitalize`}>{b.status.replace("_", " ")}</Badge>
                    <span className="text-xs text-muted-foreground">Ref: <span className="font-mono font-medium text-foreground">{b.reference_no}</span></span>
                  </div>
                  <h3 className="font-display text-xl mb-2">{b.product_title}</h3>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-teal" />{b.travel_date}</span>
                    <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-teal" />{b.adults}A {b.children > 0 && `${b.children}C`}</span>
                    <span className="text-saffron font-bold">{formatINR(b.total_amount)}</span>
                  </div>
                  {(b.pickup_location || b.special_requests) && (
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-1.5">
                      {b.pickup_location && (
                        <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-teal" />{b.pickup_location}</span>
                      )}
                      {b.special_requests && (
                        <span className="flex items-center gap-1.5"><MessageSquare className="w-4 h-4 text-teal" />{b.special_requests}</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 sm:flex-col sm:items-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadVoucher(b.id, b.reference_no)}
                    data-testid={`download-${b.reference_no}`}
                  >
                    <Download className="w-4 h-4 mr-1" /> Voucher
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => printTicket(b.id)}
                    data-testid={`print-${b.reference_no}`}
                  >
                    <Printer className="w-4 h-4 mr-1" /> Print
                  </Button>
                  {b.status !== "cancelled" && b.status !== "completed" && (
                    <Button size="sm" variant="ghost" onClick={() => cancel(b)} className="text-destructive hover:text-destructive" data-testid={`cancel-${b.reference_no}`}>
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
